/**
 * 牌桌事件音（§11.6 的听觉版）。
 *
 * 三个事件音，全部用 `haptics.ts` 已经验证过的合成音原语（`playBlip`）拼出来，
 * 不引入任何音频文件：零网络、零解码、零打包体积，也不需要处理跨域/CDN/版权。
 * AudioContext 的生命周期管理（不支持时降级、瞬时失败可恢复、iOS 手势解锁）
 * 直接复用 `haptics.ts` 已经踩过的坑，这里不重新发明一遍。
 *
 *  - deal（发牌）：一手新牌开局时响一次轻柔中音，**不逐张响**——8 人桌一次发
 *    16 张牌，逐张响会变成一串刺耳的机关枪，听感比不响还糟。
 *  - chip（下筹码）：下注/跟注/加注真正实缴时响一次柔和双击，直接由动作记录触发；
 *    即使该动作与换街同帧、帧末投入已经归零，也不会漏音。
 *  - check（过牌）：轮到的这个人选择过牌时响一声低沉短音，模拟敲桌确认的动作。
 *  - phrase（固定短语）：只在收到别人新发的固定短语时轻响，自由输入不响。
 *  - turn（轮到你）：行动权刚切到自己时响一组清楚但不刺耳的上行音。
 *
 * 全部依赖可注入，纯 node 环境可测（没有真 AudioContext / 没有真 timer）。
 */
import { resumeAudioContext, sharedAudioContext, unlockAudioContext } from './audioContext.js'
import {
  playBlip,
  realHapticTimers,
  type AudioContextFactory,
  type AudioContextLike,
  type HapticTimers,
} from './haptics.js'
import { duckBackgroundMusic } from './music.js'
import { preloadSampleSfx } from './sampleSfx.js'

export type SfxKind = 'deal' | 'chip' | 'check' | 'phrase' | 'turn'

interface Blip {
  readonly hz: number
  readonly ms: number
  readonly volume: number
  readonly waveform: 'sine' | 'triangle'
  readonly attackMs: number
  /** 同一event 音里，这一声相对第一声的延迟；省略 = 立即响 */
  readonly delayMs?: number
}

/**
 * 纸牌桌取向的参数表：动作声集中在中低频，聊天与轮次提示略高但不超过 760Hz。
 * 旧版峰值只有 0.036，在手机扬声器上几乎听不见；现在保持足够余量，同时把不同事件
 * 的峰值限制在 0.18 以下，避免连续行动时疲劳。
 * 改这张表不需要动下面的调度逻辑。
 */
const RECIPES: Readonly<Record<SfxKind, readonly Blip[]>> = {
  deal: [
    { hz: 390, ms: 72, volume: 0.11, waveform: 'sine', attackMs: 5 },
    { hz: 470, ms: 48, volume: 0.055, waveform: 'triangle', attackMs: 4, delayMs: 34 },
  ],
  chip: [
    { hz: 310, ms: 46, volume: 0.15, waveform: 'triangle', attackMs: 2 },
    { hz: 430, ms: 38, volume: 0.10, waveform: 'triangle', attackMs: 2, delayMs: 42 },
  ],
  check: [
    { hz: 185, ms: 58, volume: 0.17, waveform: 'sine', attackMs: 2 },
    { hz: 155, ms: 54, volume: 0.11, waveform: 'triangle', attackMs: 2, delayMs: 72 },
  ],
  phrase: [
    { hz: 560, ms: 70, volume: 0.095, waveform: 'sine', attackMs: 6 },
    { hz: 710, ms: 64, volume: 0.06, waveform: 'sine', attackMs: 8, delayMs: 64 },
  ],
  turn: [
    { hz: 440, ms: 150, volume: 0.14, waveform: 'sine', attackMs: 10 },
    { hz: 554, ms: 170, volume: 0.16, waveform: 'sine', attackMs: 10, delayMs: 145 },
    { hz: 659, ms: 210, volume: 0.12, waveform: 'sine', attackMs: 12, delayMs: 300 },
  ],
}

export interface SfxOptions {
  /** 设置里的「音效」开关，默认恒开；调用方一般传 `settingsStore.sound`。 */
  readonly enabled?: () => boolean
  /** 页面不可见时没必要占一次 AudioContext 调用（切后台的手机同样适用）。 */
  readonly visible?: () => boolean
  readonly audio?: AudioContextFactory
  readonly timers?: HapticTimers
  /** 测试观测点：每次尝试播放都回调一次 */
  readonly onFire?: (info: { kind: SfxKind; played: boolean }) => void
}

export interface Sfx {
  /** 触发一个事件音。返回是否真的响了（关闭 / 不可见 / 拿不到 AudioContext 都是 false）。 */
  play(kind: SfxKind): boolean
  /**
   * 在一次**真实的用户手势里同步调用**，让 AudioContext 从 suspended 变 running。
   *
   * `playBlip` 里的 `ctx.resume()` 假设「调用点在手势里」——这个假设对 `haptics.ts`
   * 自己的降级音成立（`fire()` 直接挂在按钮的 pointer 事件上），但对这个模块**不成立**：
   * `play()` 只从 `useTableFx.ts` 的 diff effect 里触发（见文件头注：diff 驱动而不是
   * 事件驱动），那是状态更新之后的一次异步 React effect，不是点击事件本身的调用栈。
   * 于是 AudioContext 在非手势上下文里被建出来、`resume()` 静默不生效、
   * 从此永远卡在 suspended——`playBlip` 仍然返回 `true`（没抛异常），
   * 于是「音效已经响了」这件事本身就是假的，实测（真机 Safari/Chrome）是彻底无声。
   * 一旦某次 resume 真的在手势里成功过，context 会保持 running，
   * 之后从 effect 里异步触发的 play() 才是真的会出声，不需要每次都在手势里。
   */
  unlock(): void
  /** 重新探测：测试用，或者用户在设置里切换后想清掉「已判定拿不到音频」的状态。 */
  reset(): void
}

export function createSfx(options: SfxOptions = {}): Sfx {
  const enabled = options.enabled ?? ((): boolean => true)
  const visible =
    options.visible ?? ((): boolean => globalThis.document?.visibilityState !== 'hidden')
  // 默认与触觉降级音、录音共用同一个 AudioContext（audioContext.ts）
  const audioFactory = options.audio ?? sharedAudioContext
  const timers = options.timers ?? realHapticTimers

  let ctx: AudioContextLike | null = null
  // 构造器不存在才判定不支持；一次瞬态播放失败不能让整局永久静音。
  let unsupported = false

  const getContext = (): AudioContextLike | null => {
    if (unsupported) return null
    // 被关掉的 context 再也起不来，重新向工厂要（共享工厂只在这种时候才新建）
    if (ctx !== null && ctx.state === 'closed') ctx = null
    ctx ??= audioFactory()
    if (ctx === null) unsupported = true
    return ctx
  }

  /**
   * ★ 被拒 / 挂着不回（不在手势里）时**留着这个 context**，下一次手势再 resume 它。
   *   上一版在这里把它扔掉，下一声就 new 一个新的、旧的也不 close：
   *   在 resume 会被拒的环境里，每一声下注 / 过牌都多出一个 AudioContext，越打越多。
   */
  const resume = (c: AudioContextLike): void => {
    void resumeAudioContext(c)
  }

  return {
    reset() {
      unsupported = false
      ctx = null
    },
    unlock() {
      const c = getContext()
      if (c === null) return
      // 不发可闻的声：这一下必须在手势的调用栈里，播出可闻的音会让「点一下屏幕」
      // 变成一次意外的提示音。只踢一段听不见的静音 buffer 再 resume（iOS 解锁流程）
      void unlockAudioContext(c)
    },
    play(kind): boolean {
      if (!enabled() || !visible() || unsupported) {
        options.onFire?.({ kind, played: false })
        return false
      }
      const c = getContext()
      if (c === null) {
        options.onFire?.({ kind, played: false })
        return false
      }
      if (c.state !== 'running') {
        // ★ 没跑起来就不排：往挂起的 context 里排的音不会丢，而是等下一次解锁时
        //   一股脑全放出来——刷新后看了半天牌、第一次点屏幕，攒下的提示音同时炸响。
        //   这里只是试着 resume（不在手势里多半不成），这一声算没响。
        resume(c)
        options.onFire?.({ kind, played: false })
        return false
      }
      const blips = RECIPES[kind]
      let ok = true
      for (const b of blips) {
        if (b.delayMs === undefined || b.delayMs <= 0) {
          ok = playBlip(c, b.hz, b.ms, b.volume, b.waveform, b.attackMs) && ok
        } else {
          // 延迟的那一声不影响本次 play() 的返回值：它属于「加分的第二声」，
          // 拿不到就拿不到，不该因为它失败就把整个通道判死。
          timers.setTimeout(
            () => playBlip(c, b.hz, b.ms, b.volume, b.waveform, b.attackMs),
            b.delayMs,
          )
        }
      }
      // AudioContext 可能只是被系统短暂打断：放下手里这个，下一声重新向工厂要。
      // 默认的共享工厂只在它被关掉（closed）时才真的新建，不会越建越多。
      if (!ok && ctx === c) ctx = null
      if (ok) duckBackgroundMusic(kind === 'turn' ? 850 : 420)
      options.onFire?.({ kind, played: ok })
      return ok
    },
  }
}

/**
 * 全应用共用实例。设置开关由调用方在 `playSfx` 里传，`sfx` 本身不依赖 store，
 * 和 `haptics.ts` 的 `sharedHaptics()` / `fireHaptic()` 是同一套分层。
 */
let shared: Sfx | null = null

export function sharedSfx(): Sfx {
  shared ??= createSfx()
  return shared
}

/** 仅供测试 */
export function __setSharedSfx(s: Sfx | null): void {
  shared = s
}

/**
 * 一行调用点。`enabled` 直接传 `useSettingsStore` 里的 `sound` 字段。
 *
 * 用法：
 * ```ts
 * playSfx('check', settings.sound)
 * ```
 */
export function playSfx(kind: SfxKind, enabled: boolean): boolean {
  if (!enabled) return false
  return sharedSfx().play(kind)
}

/**
 * 解锁共享实例的 AudioContext。调用点必须是真实用户手势的同步调用栈——
 * 具体接线见 `useTableFx.ts` 的 `attachSfxUnlock`。
 */
export function unlockSfx(): void {
  sharedSfx().unlock()
  preloadSampleSfx()
}
